/**
 * InviteClaimPage - Page to handle email invite links
 * Flow:
 * 1. Load invite info
 * 2. If invitee is existing user → Show "Sign in to continue" → After login → Redirect to space
 * 3. If invitee is new → Show email verification + name/avatar form → Claim as guest
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Mail, Users, Loader2, Link2, CheckCircle, LogIn, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { useSafeMode } from '../contexts/SafeModeContext';
import { useAuth } from '../contexts/AuthContext';
import { collaborationApi } from '../services/api';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

// Avatar grid positions (9x4 = 36 avatars from the animal avatar sheet)
const AVATARS = [
  // Row 1
  { id: 1, name: 'Lemur', row: 0, col: 0 },
  { id: 2, name: 'Fox', row: 0, col: 1 },
  { id: 3, name: 'Zebra', row: 0, col: 2 },
  { id: 4, name: 'Deer', row: 0, col: 3 },
  { id: 5, name: 'Fox Lady', row: 0, col: 4 },
  { id: 6, name: 'Bird', row: 0, col: 5 },
  { id: 7, name: 'Professor', row: 0, col: 6 },
  { id: 8, name: 'Bear', row: 0, col: 7 },
  { id: 9, name: 'Cat', row: 0, col: 8 },
  // Row 2
  { id: 10, name: 'Tiger', row: 1, col: 0 },
  { id: 11, name: 'Gazelle', row: 1, col: 1 },
  { id: 12, name: 'Beaver', row: 1, col: 2 },
  { id: 13, name: 'Giraffe', row: 1, col: 3 },
  { id: 14, name: 'Flamingo', row: 1, col: 4 },
  { id: 15, name: 'Wolf', row: 1, col: 5 },
  { id: 16, name: 'Llama', row: 1, col: 6 },
  { id: 17, name: 'Koala', row: 1, col: 7 },
  { id: 18, name: 'Bear Fancy', row: 1, col: 8 },
  // Row 3
  { id: 19, name: 'Tiger Cute', row: 2, col: 0 },
  { id: 20, name: 'Alpaca', row: 2, col: 1 },
  { id: 21, name: 'Bunny', row: 2, col: 2 },
  { id: 22, name: 'Lady', row: 2, col: 3 },
  { id: 23, name: 'Panda', row: 2, col: 4 },
  { id: 24, name: 'Donkey', row: 2, col: 5 },
  { id: 25, name: 'Pig', row: 2, col: 6 },
  { id: 26, name: 'Fox Fancy', row: 2, col: 7 },
  { id: 27, name: 'Cat Lady', row: 2, col: 8 },
  // Row 4
  { id: 28, name: 'Corgi', row: 3, col: 0 },
  { id: 29, name: 'Dog', row: 3, col: 1 },
  { id: 30, name: 'Otter', row: 3, col: 2 },
  { id: 31, name: 'Frog', row: 3, col: 3 },
  { id: 32, name: 'Platypus', row: 3, col: 4 },
  { id: 33, name: 'Dragon', row: 3, col: 5 },
  { id: 34, name: 'Sloth', row: 3, col: 6 },
  { id: 35, name: 'Koala Bow', row: 3, col: 7 },
  { id: 36, name: 'Owl', row: 3, col: 8 },
];

// Avatar image URL (9 columns x 4 rows grid)
const AVATAR_SHEET_URL = 'https://customer-assets.emergentagent.com/job_b353f328-c785-48ef-8d8b-6ae791a336a7/artifacts/99xqh56e_set-of-funny-animal-portraits-vector.jpg';

const AvatarSelector = ({ selectedAvatar, onSelect }) => {
  return (
    <div className="flex justify-center">
      <div className="grid grid-cols-6 gap-2 max-h-64 overflow-y-auto p-1">
        {AVATARS.map(avatar => (
          <button
            key={avatar.id}
            onClick={() => onSelect(avatar.id)}
            className={cn(
              "relative w-14 h-14 rounded-full overflow-hidden border-2 transition-all hover:scale-105",
              selectedAvatar === avatar.id
                ? "border-blue-500 ring-2 ring-blue-300"
                : "border-gray-200 hover:border-gray-300"
            )}
            title={avatar.name}
          >
            <div
              className="w-full h-full bg-cover bg-no-repeat"
              style={{
                backgroundImage: `url(${AVATAR_SHEET_URL})`,
                backgroundSize: '900% 400%',
                backgroundPosition: `${avatar.col * 12.5}% ${avatar.row * 33.33}%`,
              }}
            />
            {selectedAvatar === avatar.id && (
              <div className="absolute inset-0 bg-blue-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-blue-600" />
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

const InviteClaimPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { getThemeColors } = useSafeMode();
  const { user, loading: authLoading } = useAuth();
  const colors = getThemeColors();

  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [acceptingInvite, setAcceptingInvite] = useState(false);
  const [error, setError] = useState(null);
  const [inviteInfo, setInviteInfo] = useState(null);
  
  // Form state for guest claim
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [name, setName] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState(1);

  // Load invite information
  useEffect(() => {
    const loadInviteInfo = async () => {
      try {
        setLoading(true);
        const response = await collaborationApi.getInviteInfo(token);
        setInviteInfo(response.data);
      } catch (err) {
        console.error('Failed to load invite info:', err);
        setError(err.response?.data?.detail || 'This invite link is invalid or has expired');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      loadInviteInfo();
    }
  }, [token]);

  // If user is logged in and invite requires login, auto-accept
  useEffect(() => {
    const autoAccept = async () => {
      if (!authLoading && user && inviteInfo?.requires_login) {
        setAcceptingInvite(true);
        try {
          const response = await collaborationApi.acceptInvite(token);
          toast.success('Invitation accepted! Joining the space...');
          // Navigate to the collaboration space
          window.location.href = `/collaboration/${response.data.space_id}`;
        } catch (err) {
          console.error('Failed to accept invite:', err);
          setError(err.response?.data?.detail || 'Failed to accept invitation');
          setAcceptingInvite(false);
        }
      }
    };

    if (inviteInfo && !authLoading) {
      autoAccept();
    }
  }, [user, authLoading, inviteInfo, token]);

  // Handle sign in redirect
  const handleSignIn = () => {
    // Store the invite token so we can redirect back after login
    localStorage.setItem('pendingInviteToken', token);
    navigate('/login');
  };

  // Handle guest claim
  const handleClaim = async () => {
    // Validate email
    if (!email.trim()) {
      setEmailError('Please enter your email address');
      return;
    }
    
    if (email.toLowerCase() !== inviteInfo.email.toLowerCase()) {
      setEmailError('This invite was sent to a different email address');
      return;
    }

    if (!name.trim()) {
      toast.error('Please enter your name');
      return;
    }

    setClaiming(true);
    setEmailError('');
    
    try {
      const response = await collaborationApi.claimInvite(token, {
        email: email.trim(),
        name: name.trim(),
        avatar_id: selectedAvatar,
      });
      
      // Store guest session in localStorage
      localStorage.setItem('guestSession', JSON.stringify({
        token: response.data.guest_session_token,
        guestId: response.data.guest_id,
        name: response.data.name,
        avatarId: response.data.avatar_id,
        spaceId: response.data.space_id,
        role: response.data.role,
        email: email.trim(),
      }));
      
      toast.success('Welcome! Joining the collaboration space...');
      
      // Navigate to the collaboration canvas
      window.location.href = `/collaboration/${response.data.space_id}?guest=${response.data.guest_session_token}`;
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to claim invitation';
      if (errorMsg.includes('different email')) {
        setEmailError(errorMsg);
      } else if (errorMsg.includes('registered')) {
        setEmailError(errorMsg);
        // Update UI to show login button
        setInviteInfo(prev => ({ ...prev, requires_login: true }));
      } else {
        setError(errorMsg);
      }
      toast.error(errorMsg);
    } finally {
      setClaiming(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className={cn("min-h-screen flex items-center justify-center", colors.bgPrimary)}>
        <Loader2 className={cn("w-8 h-8 animate-spin", colors.textAccent)} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn("min-h-screen flex items-center justify-center p-4", colors.bgPrimary)}>
        <Card className={cn("p-8 max-w-md text-center", colors.bgCard, colors.borderPrimary)}>
          <Link2 className="w-16 h-16 mx-auto text-red-500 mb-4" />
          <h1 className={cn("text-xl font-semibold mb-2", colors.textPrimary)}>
            Invitation Not Available
          </h1>
          <p className={cn("mb-6", colors.textSecondary)}>
            {error}
          </p>
          <Button onClick={() => navigate('/')} variant="outline">
            Go to Home
          </Button>
        </Card>
      </div>
    );
  }

  // If invite requires login (existing user)
  if (inviteInfo?.requires_login) {
    // If already logged in, show accepting state
    if (user) {
      return (
        <div className={cn("min-h-screen flex items-center justify-center p-4", colors.bgPrimary)}>
          <Card className={cn("p-8 max-w-md text-center", colors.bgCard, colors.borderPrimary)}>
            <Loader2 className={cn("w-12 h-12 mx-auto animate-spin mb-4", colors.textAccent)} />
            <h1 className={cn("text-xl font-semibold mb-2", colors.textPrimary)}>
              Accepting Invitation...
            </h1>
            <p className={cn("text-sm", colors.textMuted)}>
              You&apos;ll be redirected to the collaboration space shortly.
            </p>
          </Card>
        </div>
      );
    }

    // Not logged in, show sign in prompt
    return (
      <div className={cn("min-h-screen flex items-center justify-center p-4", colors.bgPrimary)}>
        <Card className={cn("p-8 max-w-md w-full", colors.bgCard, colors.borderPrimary)}>
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mb-4">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <h1 className={cn("text-2xl font-bold mb-1", colors.textPrimary)}>
              You&apos;re Invited!
            </h1>
            <p className={cn("text-lg font-medium", colors.textAccent)}>
              {inviteInfo?.space_name}
            </p>
            <p className={cn("text-sm mt-1", colors.textMuted)}>
              {inviteInfo?.inviter_name} invited you to collaborate
            </p>
            <Badge className="mt-2" variant={inviteInfo?.role === 'editor' ? 'default' : 'secondary'}>
              {inviteInfo?.role === 'editor' ? 'Can Edit' : 'Can View'}
            </Badge>
          </div>

          <div className={cn("p-4 rounded-lg mb-6", colors.bgSecondary)}>
            <div className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="w-5 h-5" />
              <span className="font-medium">Account Required</span>
            </div>
            <p className={cn("text-sm mt-2", colors.textSecondary)}>
              The email <strong>{inviteInfo?.email}</strong> is already registered. 
              Please sign in to accept this invitation.
            </p>
          </div>

          <Button onClick={handleSignIn} className="w-full" size="lg">
            <LogIn className="w-4 h-4 mr-2" />
            Sign in to Continue
          </Button>
        </Card>
      </div>
    );
  }

  // Guest claim flow (non-registered user)
  return (
    <div className={cn("min-h-screen flex items-center justify-center p-4", colors.bgPrimary)}>
      <Card className={cn("p-8 max-w-lg w-full", colors.bgCard, colors.borderPrimary)}>
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mb-4">
            <Mail className="w-8 h-8 text-white" />
          </div>
          <h1 className={cn("text-2xl font-bold mb-1", colors.textPrimary)}>
            You&apos;re Invited!
          </h1>
          <p className={cn("text-lg font-medium", colors.textAccent)}>
            {inviteInfo?.space_name}
          </p>
          <p className={cn("text-sm mt-1", colors.textMuted)}>
            {inviteInfo?.inviter_name} invited you to collaborate
          </p>
          <Badge className="mt-2" variant={inviteInfo?.role === 'editor' ? 'default' : 'secondary'}>
            {inviteInfo?.role === 'editor' ? 'Can Edit' : 'Can View'}
          </Badge>
        </div>

        <div className="space-y-5">
          {/* Email Verification */}
          <div>
            <Label htmlFor="email" className={colors.textSecondary}>
              Confirm Your Email
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
              placeholder="Enter the email this invite was sent to"
              className={cn("mt-1", colors.bgInput, colors.borderPrimary, emailError && "border-red-500")}
            />
            {emailError && (
              <p className="text-xs text-red-500 mt-1">{emailError}</p>
            )}
            <p className={cn("text-xs mt-1", colors.textMuted)}>
              This must match the email address the invitation was sent to
            </p>
          </div>

          {/* Name Input */}
          <div>
            <Label htmlFor="name" className={colors.textSecondary}>
              Your Display Name
            </Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your name"
              className={cn("mt-1", colors.bgInput, colors.borderPrimary)}
            />
          </div>

          {/* Avatar Selection */}
          <div>
            <Label className={cn("mb-2 block", colors.textSecondary)}>
              Choose Your Avatar
            </Label>
            <AvatarSelector
              selectedAvatar={selectedAvatar}
              onSelect={setSelectedAvatar}
            />
          </div>

          {/* Join Button */}
          <Button
            onClick={handleClaim}
            disabled={claiming}
            className="w-full"
            size="lg"
          >
            {claiming ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Joining...
              </>
            ) : (
              <>
                <Users className="w-4 h-4 mr-2" />
                Join Space
              </>
            )}
          </Button>

          <p className={cn("text-xs text-center", colors.textMuted)}>
            Your contributions will be linked to your email address.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default InviteClaimPage;
